import { useSelector, useDispatch } from 'react-redux';
import { 
  selectTranslationDictionary, 
  selectCurrentLanguage,
  fetchTranslationDictionary
} from './WithcookieSlice';
import { useEffect, useCallback, useRef } from 'react';
import { getApiBaseUrl } from './WithcookieSlice';
import axios from 'axios';

// 등록 시도한 단어를 저장하는 전역 캐시 (모듈 레벨)
const registrationAttemptCache = new Set();

// 등록 제한 단어 패턴
const EXCLUDED_PATTERNS = [
  /^[0-9]+$/,  // 숫자만 있는 경우
  /[<>{}[\]]/,  // HTML 태그나 특수 문자
  /^\s+$/,      // 공백만 있는 경우
  /^(.)\1{2,}$/ // 같은 문자가 3번 이상 반복
];

// API 호출 제한을 위한 카운터 및 타이머
let registrationCounter = 0;
let registrationTimer = null;
const MAX_REGISTRATIONS_PER_SESSION = 50; // 세션당 최대 등록 가능 수
const REGISTRATION_RESET_INTERVAL = 1000 * 60 * 5; // 5분마다 카운터 초기화

/**
 * 다국어 지원 훅
 * @returns {{ 
 *   t: (text: string) => string, 
 *   currentLanguage: string,
 *   changeLanguage: (lang: string) => void,
 *   getSupportedLanguages: () => Array<{code: string, name: string}>
 * }}
 */
export const useTranslation = () => {
  const dispatch = useDispatch();
  const dictionary = useSelector(selectTranslationDictionary);
  const currentLanguage = useSelector(selectCurrentLanguage);
  const debounceTimer = useRef(null);
  const pendingRegistrations = useRef(new Set());
  const dictionaryLoadedRef = useRef(false);
  
  // 사전이 로드되었는지 확인
  useEffect(() => {
    // 사전이 로드된 경우
    if (dictionary && Object.keys(dictionary).length > 0) {
      dictionaryLoadedRef.current = true;
    }
  }, [dictionary]);
  
  // 컴포넌트 마운트 시 다국어 사전 로드 및 언어 초기화
  useEffect(() => {
    // 사전 로드
    dispatch(fetchTranslationDictionary());
    
    // API를 통해 companySettingJson 확인 및 언어 설정
    const fetchLanguageSetting = async () => {
      // 쿠키에서 currentLanguage 값 읽기
      const getCookieLanguage = () => {
        const match = document.cookie.match(/(?:^|;\s*)currentLanguage=([^;]*)/);
        return match ? match[1] : null;
      };

      try {
        // 1. 쿠키 우선 적용
        const cookieLang = getCookieLanguage();
        if (cookieLang) {
          dispatch({ type: 'withcookie/setCurrentLanguage', payload: cookieLang });
          localStorage.setItem('currentLanguage', cookieLang);
          return;
        }
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
        const response = await axios.get(`${apiBaseUrl}/api/check_current`, {
          withCredentials: true
        });
        
        // company.companySettingJson에서 default_language 확인
        if (response.data?.skin?.company?.companySettingJson) {
          try {
            let companySettings;
            
            // 문자열 또는 객체 형태 모두 처리
            if (typeof response.data.skin.company.companySettingJson === 'object') {
              companySettings = response.data.skin.company.companySettingJson;
            } else {
              companySettings = JSON.parse(response.data.skin.company.companySettingJson);
            }
            
            // 기본 언어 설정이 있는 경우 적용
            if (companySettings && companySettings.default_language) {
              const targetLang = companySettings.default_language.toUpperCase();
              
              // 언어 설정 적용
              dispatch({ type: 'withcookie/setCurrentLanguage', payload: targetLang });
              localStorage.setItem('currentLanguage', targetLang);
              
              console.log('회사 설정에서 기본 언어 설정:', targetLang);
              
              // 설정이 적용되었는지 확인
              setTimeout(() => {
                console.log('현재 적용된 언어:', localStorage.getItem('currentLanguage'));
              }, 200);
              
              return; // 언어 설정 완료
            }
          } catch (error) {
            console.error('companySettingJson 파싱 중 오류:', error);
            
            // 문자열에서 정규식으로 직접 추출 시도
            if (typeof response.data.skin.company.companySettingJson === 'string') {
              const settingStr = response.data.skin.company.companySettingJson;
              const langMatch = settingStr.match(/"default_language"\s*:\s*"([^"]+)"/);
              
              if (langMatch && langMatch[1]) {
                const lang = langMatch[1].toUpperCase();
                
                // 언어 설정 적용
                dispatch({ type: 'withcookie/setCurrentLanguage', payload: lang });
                localStorage.setItem('currentLanguage', lang);
                
                console.log('정규식으로 추출한 기본 언어 설정:', lang);
                return; // 언어 설정 완료
              }
            }
          }
        }
        
        // companySettingJson에서 언어 설정을 가져오지 못한 경우 localStorage 확인
        fallbackToLocalStorage();
        
      } catch (error) {
        console.error('API 호출 중 오류:', error);
        // API 호출 실패 시 localStorage 확인
        fallbackToLocalStorage();
      }
    };
    
    // localStorage에서 언어 설정 확인 및 적용
    const fallbackToLocalStorage = () => {
      const savedLanguage = localStorage.getItem('currentLanguage');
      if (savedLanguage) {
        // 저장된 언어 설정이 있으면 적용
        dispatch({ type: 'withcookie/setCurrentLanguage', payload: savedLanguage });
      } else {
        // 기본 언어를 한국어로 설정하고 로컬 스토리지에 저장
        dispatch({ type: 'withcookie/setCurrentLanguage', payload: 'KR' });
        localStorage.setItem('currentLanguage', 'KR');
      }
    };
    
    // 언어 설정 로직 실행
    fetchLanguageSetting();
    
    // 5분마다 등록 카운터 초기화
    if (!registrationTimer) {
      registrationTimer = setInterval(() => {
        registrationCounter = 0;
      }, REGISTRATION_RESET_INTERVAL);
    }
    
    // 컴포넌트 언마운트 시 타이머 정리
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [dispatch]);
  
  /**
   * 지원되는 언어 목록 반환
   */
  const getSupportedLanguages = useCallback(() => [
    { code: 'KR', name: '한국어' },
    { code: 'EN', name: 'English' },
    { code: 'JA', name: '日本語' },
    { code: 'ZH', name: '中文' },
    { code: 'VI', name: 'Tiếng Việt' },
    { code: 'TH', name: 'ภาษาไทย' },
    { code: 'ID_LANG', name: 'Bahasa Indonesia' },
    { code: 'HI', name: 'हिन्दी' },
    { code: 'MN', name: 'Монгол' }
  ], []);
  
  /**
   * 언어 변경 함수
   * @param {string} lang - 변경할 언어 코드
   */
  const changeLanguage = useCallback((lang) => {
    // 대문자로 변환하여 일관성 유지
    const normalizedLang = lang.toUpperCase();
    
    // 언어 코드 변경
    dispatch({ type: 'withcookie/setCurrentLanguage', payload: normalizedLang });
    
    // 로컬 스토리지에 저장
    localStorage.setItem('currentLanguage', normalizedLang);
    
    // 쿠키에도 저장 (30일 유지)
    document.cookie = `currentLanguage=${normalizedLang}; path=/; max-age=${60 * 60 * 24 * 30}`;
  }, [dispatch]);
  
  /**
   * 텍스트가 등록 가능한지 검사
   * @param {string} text - 검사할 텍스트
   * @returns {boolean} - 등록 가능 여부
   */
  const isValidForRegistration = useCallback((text) => {
    // 사전이 아직 로드되지 않은 경우 등록 시도 중단
    if (!dictionaryLoadedRef.current) {
      return false;
    }
    
    // 디버깅을 위한 로그 추가
    
    // text가 undefined 또는 null인 경우
    if (text === undefined || text === null) {
      return false;
    }
    
    // 문자열이 아닌 경우 문자열로 변환 (number 등 처리)
    const stringText = String(text);
    
    // 1. 텍스트가 비어있는 경우 (1글자 이상으로 변경)
    if (!stringText || stringText.length < 1) {
      return false;
    }

    // 2. 텍스트가 너무 긴 경우 (50자 이상인 경우)
    if (stringText.length > 50) {
      return false;
    }
    
    // 3. 이미 등록 시도한 단어인 경우
    if (registrationAttemptCache.has(stringText)) {
      return false;
    }
    
    // 4. 이미 등록 대기 중인 단어인지 확인 (pendingRegistrations에 있는 경우)
    if (pendingRegistrations.current.has(stringText)) {
      // 등록 시간 확인 (2초 이상 경과 시 다시 시도 허용)
      const registrationTime = window._translationPendingTimes?.get(stringText) || 0;
      const now = Date.now();
      
      if (now - registrationTime > 2000) { // 2초 이상 경과
        pendingRegistrations.current.delete(stringText);
        if (window._translationPendingTimes) {
          window._translationPendingTimes.delete(stringText);
        }
      } else {
        return false;
      }
    }
    
    // 5. 제외 패턴에 일치하는 경우
    for (const pattern of EXCLUDED_PATTERNS) {
      if (pattern.test(stringText)) {
        return false;
      }
    }
    
    // 6. 등록 횟수 제한 초과
    if (registrationCounter >= MAX_REGISTRATIONS_PER_SESSION) {
      console.warn(`[Translation] 세션 당 최대 등록 수(${MAX_REGISTRATIONS_PER_SESSION}개) 초과로 등록 중단`);
      return false;
    }
    
    return true;
  }, []);
  
  /**
   * 빈 번역 객체를 사전에 추가하는 함수 (즉시 중복 요청 방지)
   * @param {string} text - 추가할 텍스트
   */
  const addEmptyTranslationEntry = useCallback((text) => {
    // 사전이 아직 로드되지 않은 경우 추가 중단
    if (!dictionaryLoadedRef.current) {
      return;
    }
    
    // 디버깅을 위한 로그 추가

    // text가 undefined 또는 null인 경우
    if (text === undefined || text === null) {
      return;
    }
    
    // 문자열이 아닌 경우 문자열로 변환
    const stringText = String(text);
    
    // 이미 사전에 존재하는 경우
    if (!stringText || dictionary[stringText]) {
      return;
    }
    
    // 빈 번역 객체 생성
    const emptyTranslation = {
      KR: stringText, // 한국어는 원본 텍스트 유지
      EN: '',
      HI: '',
      JA: '',
      ID_LANG: '',
      VI: '',
      TH: '',
      ZH: '',
      MN: ''
    };
    
    // 사전 업데이트
    const updatedDictionary = { ...dictionary };
    updatedDictionary[stringText] = emptyTranslation;
    
    // 리덕스 스토어 업데이트
    dispatch({ type: 'withcookie/setTranslationDictionary', payload: updatedDictionary });
    
    // 중요: 등록 시도 캐시에 추가하는 부분 제거 (API 호출 후 성공 시에만 추가하도록 변경)
    // registrationAttemptCache.add(stringText);
    
  }, [dictionary, dispatch]);
  
  /**
   * 번역 API 호출로 새 단어 등록
   * @param {string} originalText - 번역할 원본 텍스트 (한국어)
   * @returns {Promise<any>} - API 응답 결과
   */
  const registerNewTranslation = useCallback(async (originalText) => {

    // //// 번역 등록 일단 꺼놓음
    // return null;


    // 사전이 아직 로드되지 않은 경우 등록 중단
    if (!dictionaryLoadedRef.current) {
      return null;
    }
    
    // 문자열 변환 및 기본 유효성 검사
    if (!originalText) return null;
    const stringText = String(originalText);
    
    // 길이 확인 (비어있거나 너무 긴 경우)
    if (stringText.length < 1 || stringText.length > 50) {
      return null;
    }
    
    // 이미 등록 시도한 단어인지 확인 
    if (registrationAttemptCache.has(stringText)) {
      return null;
    }
    
    // 등록 횟수 제한 확인
    if (registrationCounter >= MAX_REGISTRATIONS_PER_SESSION) {
      return null;
    }
    
    // 진행 중인 등록에 추가
    pendingRegistrations.current.add(stringText);
    // 등록 시간 저장을 위한 Map 생성 (없는 경우에만)
    if (!window._translationPendingTimes) {
      window._translationPendingTimes = new Map();
    }
    // 현재 시간 저장
    window._translationPendingTimes.set(stringText, Date.now());
    registrationCounter++;
    
    try {
      // API 요청 시작
      const apiBaseUrl = getApiBaseUrl(); // 기존 코드를 활성화
      const response = await fetch(`${apiBaseUrl}/api/translation-dictionary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Logipasta-Platform': 'admin',
          'Accept': 'application/json'
        },
        body: new URLSearchParams({
          originalText: stringText
        }),
        // 개발 환경에서는 include 대신 same-origin 사용 (CORS 이슈 해결)
        credentials: process.env.NODE_ENV === 'production' ? 'include' : 'same-origin'
      });
      
      // 응답 처리
      if (!response.ok) {
        const errorData = await response.json();
        console.error('[Translation API] 등록 오류:', response.status, errorData);
        throw new Error(errorData.message || '번역 등록 실패');
      }
      
      // 성공적인 응답 처리
      const data = await response.json();
      
      // 등록 캐시에 추가 (이미 시도한 단어로 표시)
      registrationAttemptCache.add(stringText);
      
      // 빈 번역 엔트리 추가 (사전 업데이트)
      addEmptyTranslationEntry(stringText);
      
      // 성공적으로 등록된 경우, 사전 다시 가져오기
      dispatch(fetchTranslationDictionary());
      
      return data;
    } catch (error) {
      console.error('[Translation API] 등록 오류:', error);
      return null;
    } finally {
      // 진행 중인 등록에서 제거
      pendingRegistrations.current.delete(stringText);
      // 등록 시간 정보도 함께 삭제
      if (window._translationPendingTimes) {
        window._translationPendingTimes.delete(stringText);
      }
    }
  }, [dispatch, addEmptyTranslationEntry]);
  
  /**
   * 디바운스를 적용한 번역 등록 함수
   * @param {string} text - 등록할 텍스트
   */
  const debouncedRegisterTranslation = useCallback((text) => {
    // 기본 유효성 검사
    if (!isValidForRegistration(text)) return;
    
    const stringText = String(text);
    
    // 진행 중인 등록에 추가
    pendingRegistrations.current.add(stringText);
    
    // 이전 디바운스 타이머 취소
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    // 새 디바운스 타이머 설정
    debounceTimer.current = setTimeout(() => {
      
      // 실제 API 호출 시작
      registerNewTranslation(stringText)
        .then(result => {
        })
        .catch(err => {
        })
        .finally(() => {
          // 진행 중인 등록에서 제거
          pendingRegistrations.current.delete(stringText);
          // 등록 시간 정보도 함께 삭제
          if (window._translationPendingTimes) {
            window._translationPendingTimes.delete(stringText);
          }
        });
    }, 2000); // 2초 디바운스
  }, [isValidForRegistration, registerNewTranslation]);
  
  /**
   * 즉시 실행되는 번역 등록 함수 (디바운스 없음)
   * @param {string} text - 등록할 텍스트
   */
  const registerTranslationImmediately = useCallback((text) => {
    
    // 문자열로 변환
    const stringText = String(text);
    
    // 이미 등록 시도한 단어인지 확인
    if (registrationAttemptCache.has(stringText)) {
      return;
    }
    
    // 직접 API 호출
    
    // 실제 API 호출 시작
    registerNewTranslation(stringText)
      .then(result => {
      })
      .catch(err => {
      });
  }, [registerNewTranslation]);
  
  /**
   * 번역 함수 - 입력된 텍스트를 현재 선택된 언어로 번역
   * @param {string} text - 번역할 텍스트 (한국어 기준)
   * @param {boolean} autoRegister - 사전에 없는 단어 자동 등록 여부 (기본값: true)
   * @returns {string} - 번역된 텍스트
   */
  const translate = useCallback((text, autoRegister = true) => {
    // text가 빈 문자열, null, undefined인 경우 처리
    if (text === undefined || text === null) {
      return '';  // 빈 문자열 반환
    }
    
    // 텍스트를 문자열로 변환 (숫자 등의 경우 처리)
    const stringText = String(text);
    
    // 현재 언어 대문자 변환 (코드 일관성)
    const lang = currentLanguage.toUpperCase();
    
    // 사전에 없는 단어인 경우
    if (!dictionary[stringText]) {
      // 사전이 로드되었고, 자동 등록 활성화된 경우에만 API 호출로 등록
      if (dictionaryLoadedRef.current && autoRegister) {
        debouncedRegisterTranslation(stringText);
      }
      
      // 번역되지 않은 원본 텍스트 반환
      return stringText;
    }
    
    // 사전에 있는 단어지만 현재 언어로 번역이 없는 경우
    if (!dictionary[stringText][lang]) {
      // 한국어로 번역 반환
      return dictionary[stringText]['KR'] || stringText;
    }
    
    // 현재 언어로 번역 반환
    return dictionary[stringText][lang];
  }, [dictionary, currentLanguage, debouncedRegisterTranslation]);
  
  // 공개 API 및 상태
  return {
    t: translate,
    currentLanguage,
    changeLanguage,
    getSupportedLanguages,
    registerNewTranslation: registerTranslationImmediately,
  };
};

/**
 * 전역 번역 사전을 직접 설정하는 액션 생성자
 * @param {Object} dictionary - 새 번역 사전
 */
export const setTranslationDictionary = (dictionary) => ({
  type: 'withcookie/setTranslationDictionary',
  payload: dictionary
}); 